/**
 * Shared coercion for LLM-staged field writes (Scribe / Otto `field_write`
 * events), mirroring the shape of the backend's
 * `records/agent_capabilities/field_write_coercion.py`.
 *
 * Why this exists on the frontend too: `STAGE_FIELD_WRITES` (Otto's frozen
 * Home Recommendation replay) deliberately skips the backend coercion step —
 * it replays whatever value the LLM proposed when the recommendation was
 * generated, verbatim, as a `field_write` SSE event (see OTTO.md). Live
 * chat writes go through `coerce_field_write` server-side already, so this is
 * a defensive second pass, not a replacement: it catches the frozen-action
 * gap without assuming which path a given `field_write` came from.
 *
 * Values already in persist-shape (the common case) pass through unchanged.
 */

const UUID_RE = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export type CoercionFieldKind =
  | 'table'
  | 'multi_select'
  | 'checkbox'
  | 'number'
  | 'object_reference'
  | 'other';

export type CoercionTableColumn = {
  key: string;
  kind: Exclude<CoercionFieldKind, 'table'>;
  options?: string[];
  referenceObjectTypeId?: string;
  multi?: boolean;
};

export type CoercionFieldSchema = {
  kind: CoercionFieldKind;
  /** `multi_select` — the field's allowed options (unvalidated if omitted). */
  options?: string[];
  /** `table` — column definitions, keyed by column `key`. */
  columns?: CoercionTableColumn[];
  /** `object_reference` — target ObjectType id, required to resolve codes. */
  referenceObjectTypeId?: string;
  /** `object_reference` — whether the field holds multiple references. */
  multi?: boolean;
};

/** Resolves a single ObjectRecord *code* (e.g. "P-100") to its stored UUID. */
export type ResolveObjectReferenceCode = (
  objectTypeId: string,
  code: string
) => Promise<string | undefined>;

export type CoerceFieldWriteResult = {
  value: unknown;
  /** Set when the value couldn't be confidently coerced — the raw value is
   * returned unchanged so the UI can still surface it (e.g. as an invalid
   * option) rather than silently dropping it. */
  warning?: string;
};

function decodeIfJsonString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

async function resolveCodes(
  codes: string[],
  objectTypeId: string | undefined,
  resolveObjectReferenceCode: ResolveObjectReferenceCode | undefined
): Promise<{resolved: string[]; unresolved: string[]}> {
  const resolved: string[] = [];
  const unresolved: string[] = [];
  for (const code of codes) {
    if (isUuid(code)) {
      resolved.push(code);
      continue;
    }

    if (!objectTypeId || !resolveObjectReferenceCode) {
      unresolved.push(code);
      continue;
    }

    // eslint-disable-next-line no-await-in-loop
    const uuid = await resolveObjectReferenceCode(objectTypeId, code);
    if (uuid) resolved.push(uuid);
    else unresolved.push(code);
  }

  return {resolved, unresolved};
}

/**
 * Coerces an object_reference value (single field or table cell) — resolves
 * bare codes to UUIDs, passing already-resolved UUIDs through unchanged.
 */
async function coerceObjectReference(
  rawValue: unknown,
  multi: boolean | undefined,
  referenceObjectTypeId: string | undefined,
  resolveObjectReferenceCode: ResolveObjectReferenceCode | undefined
): Promise<CoerceFieldWriteResult> {
  const value = multi ? decodeIfJsonString(rawValue) : rawValue;
  const codes = multi
    ? Array.isArray(value)
      ? value.filter((v): v is string => typeof v === 'string')
      : []
    : typeof value === 'string'
      ? [value]
      : [];

  if (codes.length === 0) return {value: rawValue};

  const {resolved, unresolved} = await resolveCodes(
    codes,
    referenceObjectTypeId,
    resolveObjectReferenceCode
  );
  if (unresolved.length > 0) {
    return {
      value: rawValue,
      warning: `Could not resolve reference code(s): ${unresolved.join(', ')}`,
    };
  }

  return {value: multi ? resolved : resolved[0]};
}

async function coerceTableCell(
  column: CoercionTableColumn,
  rawValue: unknown,
  resolveObjectReferenceCode: ResolveObjectReferenceCode | undefined
): Promise<CoerceFieldWriteResult> {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return {value: rawValue};
  }

  switch (column.kind) {
    case 'checkbox':
    case 'number':
    case 'multi_select': {
      return coerceFieldWriteForStaging(
        {kind: column.kind, options: column.options},
        rawValue,
        resolveObjectReferenceCode
      );
    }

    case 'object_reference': {
      return coerceObjectReference(
        rawValue,
        column.multi,
        column.referenceObjectTypeId,
        resolveObjectReferenceCode
      );
    }

    case 'other': {
      return {value: rawValue};
    }
  }
}

async function coerceTableRows(
  columns: CoercionTableColumn[],
  rows: unknown[],
  resolveObjectReferenceCode: ResolveObjectReferenceCode | undefined
): Promise<CoerceFieldWriteResult> {
  const columnsByKey = new Map(columns.map((c) => [c.key, c]));
  const warnings: string[] = [];
  const coercedRows = await Promise.all(
    rows.map(async (row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
      const newRow: Record<string, unknown> = {...row};
      const entries = Object.entries(row as Record<string, unknown>);
      await Promise.all(
        entries.map(async ([key, cellValue]) => {
          const column = columnsByKey.get(key);
          if (!column) return;
          const result = await coerceTableCell(
            column,
            cellValue,
            resolveObjectReferenceCode
          );
          newRow[key] = result.value;
          if (result.warning) warnings.push(`${key}: ${result.warning}`);
        })
      );
      return newRow;
    })
  );

  return {
    value: coercedRows,
    warning: warnings.length > 0 ? warnings.join('; ') : undefined,
  };
}

/**
 * Coerce a single LLM-staged field value into its persist-shape before it
 * enters form state (`pendingChanges` / react-hook-form values).
 *
 * Best-effort: when a value can't be confidently coerced (e.g. an
 * unresolvable object_reference code), the raw value is returned unchanged
 * alongside a `warning` so the caller can still render it — mirrors the
 * backend's "surface as invalid rather than silently drop" behavior.
 */
export async function coerceFieldWriteForStaging(
  schema: CoercionFieldSchema,
  rawValue: unknown,
  resolveObjectReferenceCode?: ResolveObjectReferenceCode
): Promise<CoerceFieldWriteResult> {
  switch (schema.kind) {
    case 'table':
    case 'multi_select': {
      const decoded = decodeIfJsonString(rawValue);
      if (!Array.isArray(decoded)) {
        return {
          value: rawValue,
          warning: `Expected a list, got ${typeof rawValue}.`,
        };
      }

      if (schema.kind === 'multi_select') {
        return {value: decoded};
      }

      return coerceTableRows(
        schema.columns ?? [],
        decoded,
        resolveObjectReferenceCode
      );
    }

    case 'checkbox': {
      if (typeof rawValue === 'boolean') return {value: rawValue};
      if (typeof rawValue === 'string') {
        const normalized = rawValue.trim().toLowerCase();
        if (normalized === 'true') return {value: true};
        if (normalized === 'false') return {value: false};
      }

      return {
        value: rawValue,
        warning: 'Expected true/false for this field.',
      };
    }

    case 'number': {
      if (typeof rawValue === 'number') return {value: rawValue};
      if (typeof rawValue === 'string' && rawValue.trim() !== '') {
        const parsed = Number(rawValue.trim());
        if (!Number.isNaN(parsed)) return {value: parsed};
      }

      return {value: rawValue, warning: 'Expected a number for this field.'};
    }

    case 'object_reference': {
      return coerceObjectReference(
        rawValue,
        schema.multi,
        schema.referenceObjectTypeId,
        resolveObjectReferenceCode
      );
    }

    case 'other': {
      return {value: rawValue};
    }
  }
}
